import { useRef, useState } from 'react'
import { Check, ChevronUp, Image as ImageIcon, Mic } from 'lucide-react'
import type { Card } from '../../types'
import { findTermSide } from '../../utils/cardSides'
import { isBlank, valueSide } from '../../utils/fieldValue'
import { isEligibleForLearn, learnGaps, notInStudyKind, displaySides, repeatCounts, sideKindLabel } from '../../utils/cardSideDisplay'
import { cardTranslation } from '../../utils/cardFilters'
import { useLocale } from '../../i18n/context'
import { useFillCard } from '../../hooks/useFill'
import { useSpeech } from '../../hooks/useSpeech'
import { extractErrorMessage } from '../../api/client'
import { useCardWriteFailed, useVisibleCard } from '../../hooks/useCardDisplay'
import MasteryMeter from './MasteryMeter'
import { ForgotWordButton } from './MasteryResetConfirm'
import NotInStudyMark from './NotInStudyMark'
import UnsavedMark from './UnsavedMark'
import SpeakerButton from '../learning/SpeakerButton'

interface Props {
  card: Card
  deckLanguage: string
  /** Omit for read-only viewers (system deck / public deck non-owner). Gates
   * Fill — the one owner action the expanded state carries. It does not gate
   * expansion: the expanded state is read-only by definition (brief §5.2), and
   * it is where the learner's own reset lives (ADR-098), which writes only their
   * progress, never the deck. Selection and editing are gated by the caller. */
  isOwner?: boolean
  onLongPress?: () => void
  selectMode?: boolean
  selected?: boolean
  onToggleSelect?: () => void
  /**
   * Editing mode (ADR-067 Decision 2 / brief §3.4): a tap places the caret in
   * the field it landed on instead of expanding the card for reading. The
   * read-only expanded state does not occur in this mode AT ALL — that is the
   * whole content of the mode split, so it is suppressed rather than merely
   * unreachable.
   */
  editingMode?: boolean
  /**
   * Called with the field the tap landed on and, where the platform can tell
   * us, the character offset inside that field's text. The caller turns this
   * into the active card (ActiveVocabCard) with that field focused and the
   * caret at that offset. Omit to leave a tap inert in editing mode.
   */
  onActivateField?: (fieldKey: string, caretOffset: number | null) => void
  /**
   * The card a `?card=` deep link named, marked briefly on arrival so the
   * reader can see which row the link meant (the same mark the desktop row
   * wears).
   *
   * Styling and nothing else — it carries no behaviour, changes no mode, takes
   * no caret and raises no keyboard. That is what lets a link from the Quick
   * Lookup sheet land on a system deck or on someone else's public deck: the
   * mark is a fact about the arrival, not a state the card is in.
   */
  highlighted?: boolean
}

/** The Term's stand-in field key — Term has no side-B identity to borrow. */
export const TERM_FIELD_KEY = 'term'

// Deck-feed vocabulary card, collapsed and expanded (docs/design/deck-feed-
// redesign §5.1/§5.2, ADR-067 Decision 2). The third state — active, with its
// fields open for typing — is a separate component (ActiveVocabCard): this one
// is what a NON-active card looks like, identically in both modes (§5.1), and
// all that changes between them is what a tap does. In viewing mode a tap
// expands for reading; in editing mode it activates this card with the caret
// in the tapped field. There is no escape hatch to a second editor: the
// expanded state's `Edit card` button opened the full-screen one, and both
// went with ADR-067 Decision F. Long text is edited in place, under the
// caret-keeping rule the retirement plan §3.4 writes down.
export default function VocabCard({
  card: cardProp, deckLanguage, isOwner = false,
  onLongPress, selectMode = false, selected = false, onToggleSelect,
  editingMode = false, onActivateField, highlighted = false,
}: Props) {
  // The card as it should be SHOWN — the person's own unconfirmed change if
  // there is one, otherwise the freshest server answer (ADR-072 Decision 1).
  // The prop comes from the deck list's query, which is two round trips behind
  // a change that has just been made: the write is not issued until editing
  // finishes and the list is not re-read until that write has answered. Reading
  // through the draft is what makes a finished card, an added field, a rename
  // and a reorder appear the moment they are made rather than a second later.
  //
  // Nothing is painted into the list's cache to achieve that — this row reads
  // the same draft the editing surface holds, so there is no second copy that
  // could disagree with it (Decision 5).
  const card = useVisibleCard(cardProp)
  const writeFailed = useCardWriteFailed(card.id)
  const { tr } = useLocale()
  const cb = tr.cardBubble
  const ce = tr.cardEditor

  const [expanded, setExpanded] = useState(false)
  // Suppressed rather than just unreachable in editing mode: a card left
  // expanded in viewing mode must not stay expanded across the mode switch,
  // since the expanded state is defined as read-only (§5.2).
  const showExpanded = expanded && !editingMode
  const term = findTermSide(card.sides)?.content ?? ''
  // Authored sides, kept separate from the displayed values below: only an
  // authored side is a thing a tap can put a caret into. A corpus translation
  // (ADR-050, `card.translation` with no Translation side) is displayed but
  // has no field of its own, so a tap on it falls back like any other
  // non-field part of the card.
  // ADR-104: the card's definition is its first NON-BLANK Definition. A blank
  // field in front of it is not the definition and does not hide it. With no
  // translation value, the first (blank) Translation field is still the tap
  // target that fills it in.
  const translationSide = valueSide(card.sides, 'translation')
    ?? card.sides.find((s) => s.kind === 'translation') ?? null
  const definitionSide = valueSide(card.sides, 'definition') ?? null
  const translation = cardTranslation(card)
  const definition = definitionSide?.content ?? null
  // ADR-064: eligible for Learn with a non-blank Definition or Translation —
  // either suffices, and that admission rule is untouched here. The mark
  // below is a SEPARATE, stricter ladder: it keeps naming whichever of the
  // pair is still missing until both are present, even once the card is
  // already eligible on one field alone — "the marker's job is finished"
  // only once neither is left to ask for.
  const eligible = isEligibleForLearn(card)
  // The mark's label is its remedy ("Add a translation") — an invitation to a
  // write, with no statement form to fall back to. On a deck the reader cannot
  // write to it is withheld, not reworded: whether the card is in study is
  // still said by the card's dashed border, which is not an invitation and does
  // not depend on who owns the deck. And on a system deck a missing translation
  // is usually no gap at all — translations resolve to the reader's own
  // language, and none existing is the correct state (ADR-050).
  const gapKind = isOwner ? notInStudyKind(learnGaps(card)) : null
  const sides = displaySides(card)
  const repeats = repeatCounts(card, ce)
  const hasImage = sides.some((d) => d.side.kind === 'image' && d.side.content.trim())
  const hasAudio = sides.some((d) => d.side.kind === 'audio' && d.side.content.trim())

  const { speak, cancelSilently, isSpeaking } = useSpeech(deckLanguage)
  function handleSpeak() {
    if (isSpeaking) { cancelSilently(); return }
    speak(term)
  }

  // Fill lives in the expanded state only (not the collapsed row, which
  // never grows beyond its fixed skeleton) and is not routed through
  // multi-select — it is a direct per-card action alongside the evidence
  // for why the card needs it (missing Definition/Translation), distinct
  // from the not-in-study mark itself: the mark states a condition, Fill is
  // one way to resolve it.
  const fillMutation = useFillCard()
  const [fillError, setFillError] = useState<string | null>(null)
  function handleFill() {
    if (fillMutation.isPending) return
    setFillError(null)
    fillMutation.mutate(card.id, {
      onSuccess: (data) => {
        if (data.ai_budget_exhausted) setFillError(tr.deckDetail.autofillErrorRateLimit)
      },
      onError: (err) => setFillError(extractErrorMessage(err, cb.fillErrorGeneric)),
    })
  }

  // Long-press (Prompt 8) — same 500ms/10px-jitter gesture the previous
  // collapsed row used. A plain tap toggles select in select mode, otherwise
  // it expands/collapses; there is no swipe-to-edit gesture any more —
  // ADR-067 Decision 2 makes "tap expands" the whole story in viewing mode,
  // and editing is reached only through the explicit button below.
  // Every reader can expand a card for reading. The inherited CardBubble rule
  // made non-owner cards inert because that bubble "already shows all of a
  // card's content inline"; this collapsed row does not (definition clamped to
  // two lines, repeats counted rather than shown), so the premise is gone.
  // What stays owner-only — selection, editing, Fill — is gated where it lives.
  const touchStartRef = useRef({ x: 0, y: 0 })
  const suppressClickRef = useRef(false)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressFiredRef = useRef(false)
  const LONG_PRESS_MS = 500
  const LONG_PRESS_JITTER = 10

  function clearLongPressTimer() {
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null }
  }
  function handleTouchStart(e: React.TouchEvent) {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    longPressFiredRef.current = false
    if (onLongPress && !selectMode) {
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null
        longPressFiredRef.current = true
        suppressClickRef.current = true
        onLongPress()
      }, LONG_PRESS_MS)
    }
  }
  function handleTouchMove(e: React.TouchEvent) {
    if (!longPressTimerRef.current) return
    const dx = e.touches[0].clientX - touchStartRef.current.x
    const dy = e.touches[0].clientY - touchStartRef.current.y
    if (Math.abs(dx) > LONG_PRESS_JITTER || Math.abs(dy) > LONG_PRESS_JITTER) clearLongPressTimer()
  }
  function handleTouchEnd() {
    clearLongPressTimer()
  }
  // The field a tap lands on when it lands on no field in particular (the
  // mastery meter, the padding, the not-in-study pill). The first side-B field
  // is the useful default — it is what the reader was most likely reaching for
  // — and a word-only card falls back to its Term, the one field every card
  // has. Never reorders anything: this only chooses what to focus.
  const fallbackFieldKey =
    displaySides(card).find((d) => d.side.kind !== 'image' && d.side.kind !== 'audio')?.side.id
    ?? TERM_FIELD_KEY

  function handleActivate(e: React.MouseEvent | React.KeyboardEvent) {
    if (!onActivateField) return
    const target = 'target' in e ? (e.target as HTMLElement | null) : null
    const host = target?.closest?.('[data-field-key]') as HTMLElement | null
    const fieldKey = host?.dataset.fieldKey ?? fallbackFieldKey
    const caret = host && 'clientX' in e ? caretOffsetAt(host, e.clientX, e.clientY) : null
    onActivateField(fieldKey, caret)
  }

  function handleClick(e: React.MouseEvent | React.KeyboardEvent) {
    if (suppressClickRef.current) { suppressClickRef.current = false; return }
    if (longPressFiredRef.current) return
    if (selectMode) { onToggleSelect?.(); return }
    if (editingMode) { handleActivate(e); return }
    setExpanded((v) => !v)
  }

  const notInStudyPill = (gapKind !== null || writeFailed) && (
    <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6 }}>
      {gapKind !== null && <NotInStudyMark gapKind={gapKind} />}
      {/* Only after a failed write, and it travels with the row rather than
          with the editing surface: the gesture that finishes a card is usually
          the gesture that closes it, so the collapsed row is where the person
          is standing when the failure lands (ADR-072 Decision 4). */}
      {writeFailed && <UnsavedMark />}
    </div>
  )

  return (
    <div
      data-vocab-card=""
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="button"
      aria-expanded={!editingMode ? showExpanded : undefined}
      aria-pressed={selectMode ? selected : undefined}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e) } }}
      style={{
        display: 'flex', flexDirection: 'column', gap: 5,
        background: 'var(--bg-surface)',
        // The deep link's mark takes the border and adds a soft ring — the same
        // two properties, in the same tokens, the desktop row's mark uses. It
        // outranks the not-eligible dash for as long as it lasts: the reader
        // followed a link to THIS card, and the dash is still there afterwards.
        border: highlighted || (selectMode && selected) ? '1px solid var(--accent)'
          : eligible ? '1px solid var(--border)' : '1px dashed var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: highlighted ? '0 0 0 3px var(--accent-soft)'
          : eligible ? 'var(--shadow-card)' : 'none',
        transition: 'border-color 0.12s, box-shadow 0.12s',
        padding: showExpanded ? '16px 16px 18px' : '12px 14px',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
        {selectMode && (
          <span
            aria-hidden="true"
            style={{
              width: 21, height: 21, borderRadius: 'var(--radius-pill)', flexShrink: 0, marginTop: 2,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: selected ? 'var(--accent-fill)' : 'var(--bg-surface)',
              border: selected ? '1px solid var(--accent-fill)' : '1px solid var(--border)',
            }}
          >
            {selected && <Check size={13} strokeWidth={3.2} color="#fff" />}
          </span>
        )}

        <span onClick={(e) => e.stopPropagation()}>
          {showExpanded ? (
            <SpeakerButton onClick={handleSpeak} isSpeaking={isSpeaking} />
          ) : (
            <button
              onClick={handleSpeak}
              aria-label={tr.learn.pronounceWord}
              style={{
                width: 30, height: 30, borderRadius: 'var(--radius-pill)', flexShrink: 0, marginTop: 2,
                background: 'var(--accent-soft)', color: 'var(--accent)', border: 'none',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0,
              }}
            >
              <Mic size={14} strokeWidth={2} />
            </button>
          )}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            data-field-key={TERM_FIELD_KEY}
            style={{
              fontFamily: 'var(--font-content)', fontWeight: showExpanded ? 700 : 600,
              fontSize: showExpanded ? 26 : 19, color: 'var(--text-primary)',
              letterSpacing: '-0.01em', lineHeight: 1.2,
            }}
          >
            {term}
          </div>
          {showExpanded ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 7, flexWrap: 'wrap' }}>
              <MasteryMeter level={card.mastery_level ?? 0} width={64} />
              {/* ADR-098 — see ForgotWordButton for when it is offered. */}
              <ForgotWordButton
                cardId={card.id}
                deckId={card.deck_id}
                term={term}
                masteryLevel={card.mastery_level}
                isMastered={card.is_mastered}
              />
            </div>
          ) : (
            translation && (
              <div
                data-field-key={translationSide?.id}
                style={{ fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 2 }}
              >
                {translation}
              </div>
            )
          )}
        </div>

        {showExpanded ? (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(false) }}
            aria-label={cb.collapseCardAria}
            style={{ background: 'none', border: 'none', color: '#aab2ba', cursor: 'pointer', padding: 4, flexShrink: 0 }}
          >
            <ChevronUp size={20} />
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 7, flexShrink: 0, paddingTop: 3 }}>
            <MasteryMeter level={card.mastery_level} width={56} />
            {(hasImage || hasAudio || repeats.length > 0) && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                {hasImage && (
                  <span style={{
                    width: 22, height: 22, borderRadius: 6, background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'var(--text-secondary)',
                  }}>
                    <ImageIcon size={11} strokeWidth={2.2} />
                  </span>
                )}
                {hasAudio && <Mic size={13} strokeWidth={2} color="var(--text-secondary)" />}
                {repeats.map((r) => (
                  <span key={r.kind} style={{
                    fontFamily: 'var(--font-ui)', fontSize: 10.5, fontWeight: 600,
                    letterSpacing: '0.06em', color: '#9aa3ab', textTransform: 'uppercase', whiteSpace: 'nowrap',
                  }}>
                    {cb.repeatCount(r.label, r.count)}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!showExpanded && (
        gapKind !== null ? notInStudyPill : (
          definition && (
            <div
              data-field-key={definitionSide?.id}
              style={{
                fontFamily: 'var(--font-ui)', fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}
            >
              {definition}
            </div>
          )
        )
      )}

      {showExpanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 4 }}>
          <div style={{ height: 1, background: 'var(--border)' }} />

          {gapKind !== null && notInStudyPill}

          {translation && (
            <ExpandedField eyebrow={ce.translationLabel}>
              <div style={{ fontFamily: 'var(--font-content)', fontSize: 18, color: 'var(--text-primary)', lineHeight: 1.35 }}>
                {translation}
              </div>
            </ExpandedField>
          )}

          {definition && (
            <ExpandedField eyebrow={ce.definitionLabel}>
              <div style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.55 }}>
                {definition}
              </div>
            </ExpandedField>
          )}

          {/* The block above already shows kind translation/definition's
              VALUE field (ADR-104: the first non-blank one) — that one is
              skipped here so it isn't rendered twice, and so is a blank
              Translation or Definition, which has nothing to read.
               A second Definition or
              Translation on the same card is not dropped though: it goes
              through the same subordinate/STUDIED treatment Example and every
              other repeating kind already gets below, per the reading state's
              "stored order, repeats included" rule. */}
          {sides
            .filter((d) => !((d.side.kind === 'translation' || d.side.kind === 'definition') && (d.isValue || isBlank(d.side.content))))
            .map((d) => (
              <ExpandedSideField key={d.side.id} display={d} label={sideKindLabel(d.side, ce)} term={term} cb={cb} />
            ))}

          {isOwner && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(!definition || !translation) && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleFill() }}
                  disabled={fillMutation.isPending}
                  style={{
                    alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'transparent', border: '1px solid var(--border)', color: 'var(--accent)',
                    borderRadius: 'var(--radius-pill)', padding: '6px 14px',
                    fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 12.5,
                    cursor: fillMutation.isPending ? 'default' : 'pointer', opacity: fillMutation.isPending ? 0.7 : 1,
                  }}
                >
                  {fillMutation.isPending ? cb.fillLoading : cb.fillBtn}
                </button>
              )}
              {fillError && (
                <div style={{ fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--danger)' }}>{fillError}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Character offset inside a tapped field's text, so editing mode can honour
// "the caret goes where the finger landed" (brief §7.1) rather than dumping it
// at the end of the value. The offset is an index into the SAME string the
// text field will hold, so the collapsed row's smaller type does not skew it —
// only the character position is carried across, never a pixel coordinate.
// Two spellings exist (`caretRangeFromPoint` in Blink/WebKit, the standard
// `caretPositionFromPoint` in Gecko) and either may be absent; a null return
// means "caret at the end", which is the honest fallback.
function caretOffsetAt(host: HTMLElement, x: number, y: number): number | null {
  type CaretDoc = Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null
  }
  const doc = document as CaretDoc
  try {
    if (typeof doc.caretRangeFromPoint === 'function') {
      const range = doc.caretRangeFromPoint(x, y)
      if (range && range.startContainer.nodeType === Node.TEXT_NODE && host.contains(range.startContainer)) {
        return range.startOffset
      }
    } else if (typeof doc.caretPositionFromPoint === 'function') {
      const pos = doc.caretPositionFromPoint(x, y)
      if (pos && pos.offsetNode.nodeType === Node.TEXT_NODE && host.contains(pos.offsetNode)) {
        return pos.offset
      }
    }
  } catch {
    // Unsupported or cross-origin-ish edge — fall through to caret at end.
  }
  return null
}

function ExpandedField({ eyebrow, badge, children }: { eyebrow: string; badge?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <div style={{ fontFamily: 'var(--font-ui)', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', color: '#9aa3ab', textTransform: 'uppercase' }}>
          {eyebrow}
        </div>
        {badge}
      </div>
      {children}
    </div>
  )
}

// One non-translation/definition side in the expanded card. A subordinate
// (non-authoritative) field of a repeating kind is pushed behind a hairline
// rail, stepped down in type, and lightened — three simultaneous shifts per
// the brief's "quieter, not damaged" rule — with the eyebrow saying so in
// words too. The STUDIED badge lands only on the authoritative field of a
// kind that actually repeats (ADR-066 §2); a card with a single Example
// never shows it.
function ExpandedSideField({
  display, label, term, cb,
}: {
  display: { side: { id: string; kind?: string | null; content: string }; kindRepeats: boolean; ordinalInKind: number; isValue: boolean }
  label: string
  term: string
  cb: {
    studiedBadge: string
    notStudiedLabel: string
    ordinalPosition: (n: number) => string
    audioFieldCaption: string
  }
}) {
  const { side, kindRepeats, ordinalInKind, isValue } = display
  // The field Learn studies (ADR-104 Decision 6): never a blank one.
  const authoritative = isValue
  const subordinate = kindRepeats && !authoritative

  if (side.kind === 'image') {
    return (
      <ExpandedField eyebrow={label}>
        <img
          src={side.content}
          alt={term}
          style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 12, border: '1px solid var(--border)' }}
        />
      </ExpandedField>
    )
  }

  if (side.kind === 'audio') {
    return (
      <ExpandedField eyebrow={label}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Own control, deliberately not SpeakerButton — this plays a user
              attachment, not the term's TTS pronunciation (design brief
              §18). */}
          <audio controls src={side.content} style={{ width: '100%', height: 36 }} />
          <div style={{ fontSize: 11, color: '#9aa3ab' }}>{cb.audioFieldCaption}</div>
        </div>
      </ExpandedField>
    )
  }

  const eyebrow = subordinate
    ? `${label} · ${cb.ordinalPosition(ordinalInKind)} · ${cb.notStudiedLabel}`
    : label
  const badge = authoritative && kindRepeats && (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: 'var(--radius-pill)',
      padding: '2px 8px', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
    }}>
      <Check size={9} strokeWidth={3.2} />
      {cb.studiedBadge}
    </span>
  )

  const content = (
    <div style={{
      fontFamily: 'var(--font-content)', fontStyle: side.kind === 'example' ? 'italic' : 'normal',
      fontSize: subordinate ? 13.5 : 15, color: subordinate ? 'var(--text-secondary)' : 'var(--text-primary)',
      lineHeight: 1.5,
    }}>
      {side.content}
    </div>
  )

  if (!subordinate) {
    return <ExpandedField eyebrow={eyebrow} badge={badge}>{content}</ExpandedField>
  }

  return (
    <div style={{ display: 'flex', gap: 11 }}>
      <span style={{ width: 2, borderRadius: 2, background: 'var(--border)', flexShrink: 0, marginTop: 3, marginBottom: 3 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
        <div style={{ fontFamily: 'var(--font-ui)', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', color: '#b3bbc2', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
          {eyebrow}
        </div>
        {content}
      </div>
    </div>
  )
}
